import {
  AccountHolderType,
  Currency,
  ExternalBankAccountAccountType,
  PayoutDestinationType,
  PreferredPayoutSpeed
} from "@wingspanhq/wingspan-api-sdk";
import { WSMutationConfig } from "@ws-react-query";
import { externalBankAccountsApi, payoutsApi } from "@api";
import { queriesExternalBankAccounts, queriesPayouts } from "@query";
import { selectorOwnPayoutSettings } from "@selectors/selectorOwnPayoutSettings";
import { IdempotencyKey } from "@utils/idempotencyKey";
import { INVOICE_ACCOUNT_NAME } from "../../../Invoices/utils";
import { useWSMutation } from "../../../query/helpers";
import { WSServiceError } from "../../../utils/serviceHelper";
import { FormData } from "./types";

export const useMutationSubmit = (
  options?: { skipPayoutSettingsUpdate?: boolean },
  config?: WSMutationConfig<void>
) => {
  return useWSMutation<void, WSServiceError, { data: FormData }>(
    async ({ data }) => {
      const idempotencyKey = new IdempotencyKey({
        accountNumber: data.accountNumber,
        routingNumber: data.routing
      });

      const externalBankAccount =
        await externalBankAccountsApi.createExternalBankAccount({
          idempotencyKey: idempotencyKey.value,
          externalBankAccountCreate: {
            routingNumber: data.routing,
            ...(data.routingSecondary && {
              routingSecondary: data.routingSecondary
            }),
            accountNumber: data.accountNumber,
            accountType: ExternalBankAccountAccountType.CHECKING,
            accountHolderType: AccountHolderType.INDIVIDUAL,
            accountHolderName: "",
            country: data.country,
            currency: data.currency as Currency,
            nickname: INVOICE_ACCOUNT_NAME,
            ...(data.beneficiaryNameLocal && {
              beneficiaryNameLocal: data.beneficiaryNameLocal
            }),
            ...(data.beneficiaryBankAccountType && {
              beneficiaryBankAccountType: data.beneficiaryBankAccountType
            })
          }
        });

      idempotencyKey.destroy();

      if (!options?.skipPayoutSettingsUpdate) {
        const payoutSettingsList =
          await queriesPayouts.listPayoutSettings.fetchQueryData();
        const payoutSettingsId =
          selectorOwnPayoutSettings(payoutSettingsList)?.id;

        if (payoutSettingsId) {
          await payoutsApi.updatePayoutSettings({
            payoutSettingsId,
            payoutSettingsUpdate: data.useAsDefaultIPMethod
              ? {
                  preferredPayoutSpeed: PreferredPayoutSpeed.INSTANT,
                  instantDestination: {
                    type: PayoutDestinationType.EXTERNAL_BANK_ACCOUNT,
                    id: externalBankAccount.id
                  }
                }
              : {
                  preferredPayoutSpeed: PreferredPayoutSpeed.STANDARD,
                  standardDestination: {
                    type: PayoutDestinationType.EXTERNAL_BANK_ACCOUNT,
                    id: externalBankAccount.id
                  }
                }
          });
        }
      }
    },
    {
      dependencies: [
        queriesExternalBankAccounts.listExternalBankAccounts.queryKeyBase,
        queriesPayouts.listPayoutSettings.queryKeyBase,
        queriesPayouts.getPayoutSettings.queryKeyBase
      ],
      ...config
    }
  );
};
